import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, KeyRound, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export default function ForgotPassword({ open, onClose, initialEmail = "" }) {
  const [step, setStep] = useState("request"); // request | reset
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep("request"); setCode(""); setNewPassword(""); setBusy(false); };
  const close = () => { onClose(); setTimeout(reset, 200); };

  const requestCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If that email is registered, a reset code is on its way 📧");
      setStep("reset");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email, code: code.trim(), new_password: newPassword });
      toast.success("Password updated! You can now sign in. 🎉");
      close();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-[#9B5DE5]" /> Reset your password
          </DialogTitle>
          <DialogDescription>
            {step === "request"
              ? "Enter your account email and we'll send you a 6-digit reset code."
              : `Enter the 6-digit code sent to ${email} and choose a new password.`}
          </DialogDescription>
        </DialogHeader>

        {step === "request" ? (
          <form onSubmit={requestCode} className="space-y-4 mt-2">
            <div>
              <Label htmlFor="fp-email">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="fp-email" data-testid="forgot-email-input" type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="rounded-xl h-12 pl-10" />
              </div>
            </div>
            <Button type="submit" data-testid="forgot-send-btn" disabled={busy}
              className="w-full h-12 rounded-xl font-bold bg-[#9B5DE5] hover:bg-[#8349c9] text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset code"}
            </Button>
          </form>
        ) : (
          <form onSubmit={submitReset} className="space-y-4 mt-2">
            <div>
              <Label htmlFor="fp-code">Reset code</Label>
              <Input id="fp-code" data-testid="reset-code-input" required value={code}
                onChange={(e) => setCode(e.target.value)} placeholder="123456" maxLength={6}
                className="rounded-xl h-12 mt-1 tracking-[0.5em] text-center font-mono text-lg" />
            </div>
            <div>
              <Label htmlFor="fp-newpass">New password</Label>
              <Input id="fp-newpass" data-testid="reset-newpass-input" type="password" required minLength={6} value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)} placeholder="At least 6 characters" className="rounded-xl h-12 mt-1" />
            </div>
            <Button type="submit" data-testid="reset-submit-btn" disabled={busy}
              className="w-full h-12 rounded-xl font-bold bg-[#FF5A79] hover:bg-[#e04865] text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
            </Button>
            <button type="button" data-testid="reset-back-btn" onClick={() => setStep("request")}
              className="flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800 mx-auto">
              <ArrowLeft className="h-4 w-4" /> Use a different email
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
